import { useAuth } from '@/hooks/auth.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import { useEffect, useRef } from 'react';
import { IoMicOutline } from 'react-icons/io5';
import { useCurrentTime, formatTime } from '@/hooks/current-time.hook';

interface Message {
  id: string; // Changed to string to match ChatbotUI
  type: 'bot' | 'user';
  content: string;
  timestamp: string; // Display time like "10:30 AM"
  sentAt: string; // ISO date string for date calculations
}

interface ChatMessagesProps {
  userName?: string;
  messages?: Message[];
  onSendMessage?: (message: string) => void;
  onLoadMore?: () => void;
  showGreeting?: boolean;
  isTyping?: boolean;
  typingBlack?: string;
  typingWhite?: string;
}

// Utility function to get date label based on timestamp
const getDateLabel = (timestamp: string): string => {
  const messageDate = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time to midnight for accurate date comparison
  const resetTime = (date: Date) => {
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const messageDateOnly = resetTime(new Date(messageDate));
  const todayOnly = resetTime(new Date(today));

  // Calculate difference in days
  const diffTime = todayOnly.getTime() - messageDateOnly.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays > 1 && diffDays <= 7) {
    return 'Last Week';
  } else if (diffDays > 7 && diffDays <= 30) {
    return 'Last Month';
  } else {
    // Format as "MMM DD, YYYY" for older messages
    return messageDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
};

// Group messages by date label
interface MessageGroup {
  label: string;
  messages: Message[];
}

const groupMessagesByDate = (messages: Message[]): MessageGroup[] => {
  const groups: MessageGroup[] = [];
  let currentLabel = '';
  let currentMessages: Message[] = [];

  messages.forEach((message) => {
    const label = getDateLabel(message.sentAt);

    if (label !== currentLabel) {
      // Save previous group if it exists
      if (currentMessages.length > 0) {
        groups.push({ label: currentLabel, messages: currentMessages });
      }
      // Start new group
      currentLabel = label;
      currentMessages = [message];
    } else {
      // Add to current group
      currentMessages.push(message);
    }
  });

  // Add the last group
  if (currentMessages.length > 0) {
    groups.push({ label: currentLabel, messages: currentMessages });
  }

  return groups;
};

const ChatMessages = ({
  userName = 'User',
  messages = [],
  onSendMessage,
  onLoadMore,
  showGreeting = false,
  isTyping = false,
  typingBlack,
  typingWhite,
}: ChatMessagesProps) => {
  const { fetchProfile } = useAuth();
  const { user, token } = useRootSelector((state) => state.auth);
  const currentTime = useCurrentTime();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef<number>(0); // Changed from Infinity to 0
  const isLoadingMore = useRef<boolean>(false);
  const isInitialLoad = useRef<boolean>(true); // Track if this is first load

  // Group messages by date
  const messageGroups = groupMessagesByDate(messages);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const message = formData.get('message') as string;
    if (message.trim() && onSendMessage) {
      onSendMessage(message);
      e.currentTarget.reset();
    }
  };

  const fullName = user ? `${user.firstName} ${user.lastName}` : userName;

  useEffect(() => {
    // Chỉ fetch profile nếu có token nhưng chưa có user
    if (token && !user) {
      fetchProfile();
    }
  }, [token, user, fetchProfile]);

  // Auto-scroll to bottom on initial load or when near bottom
  useEffect(() => {
    if (messagesContainerRef.current && messages.length > 0) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 200;

      // Always scroll on initial load, or if user is near bottom
      if (isInitialLoad.current || isNearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        isInitialLoad.current = false; // Mark as no longer initial load

        // Initialize lastScrollTop after scroll
        setTimeout(() => {
          if (messagesContainerRef.current) {
            lastScrollTop.current = messagesContainerRef.current.scrollTop;
          }
        }, 100);
      }
    }
  }, [messages]);

  // Handle scroll to load more messages (only when scrolling UP to the TOP)
  const handleScroll = () => {
    if (messagesContainerRef.current && !isLoadingMore.current) {
      const { scrollTop } = messagesContainerRef.current;

      // Check if scrolling UP (scrollTop is decreasing)
      const isScrollingUp = scrollTop < lastScrollTop.current;

      // ONLY load more if: scrolling UP AND near top AND greeting NOT shown (not at beginning)
      if (isScrollingUp && scrollTop < 100 && onLoadMore && !showGreeting) {
        isLoadingMore.current = true;
        onLoadMore();

        // Reset loading flag after a delay to prevent multiple calls
        setTimeout(() => {
          isLoadingMore.current = false;
        }, 1000);
      }

      // Update last scroll position
      lastScrollTop.current = scrollTop;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat Messages Area */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-[2.7rem] pt-[2rem]"
      >
        {/* Initial Bot Message - Only show when no messages OR all messages loaded */}
        {showGreeting && (
          <>
            {/* Today Header for greeting */}
            <div className="text-center mb-6">
              <p className="text-[#9CA3AF] dark:text-[#6B7280] text-[1.2rem]">Today</p>
            </div>

            <div className="mb-6">
              <div className="bg-[#DEDEDE] dark:bg-[#2A2B2B] border border-[#DEDEDE] dark:border-[#9F9F9F] rounded-[1.2rem] px-6 py-4 max-w-[80%] inline-block">
                <p className="text-[#FFFFFF] text-[1.4rem] font-medium mb-1">Hi {fullName} 👋!</p>
                <p className="text-[#FFFFFF] text-[1.4rem] leading-relaxed">
                  I'm your Nezenx AI assistant. How can I help you with your finances today?
                </p>
                <p className="text-[#9CA3AF] text-[1rem] mt-2">{formatTime(currentTime)}</p>
              </div>
            </div>
          </>
        )}

        {/* Grouped Messages by Date */}
        {messageGroups.map((group, groupIndex) => (
          <div key={`group-${groupIndex}`}>
            {/* Date Header for each group */}
            <div className="text-center mb-6">
              <p className="text-[#9CA3AF] dark:text-[#6B7280] text-[1.2rem]">{group.label}</p>
            </div>

            {/* Messages in this group */}
            {group.messages.map((message) => (
              <div
                key={message.id}
                className={`mb-4 ${message.type === 'user' ? 'text-right' : 'text-left'}`}
              >
                <div
                  className={`inline-block px-6 py-4 rounded-[1.2rem] max-w-[80%] ${
                    message.type === 'bot'
                      ? 'bg-[#FAFAFA] dark:bg-[#2A2A2A] text-custom border border-[#DEDEDE] dark:border-[#9F9F9F]'
                      : 'bg-[#4BD2A7] text-[#000000]'
                  }`}
                >
                  <p className="text-[1.4rem] leading-relaxed">{message.content}</p>
                  <p
                    className={`text-[1rem] mt-2 ${
                      message.type === 'bot' ? 'text-[#9CA3AF]' : 'text-[#0C0E12]'
                    }`}
                  >
                    {message.timestamp}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="mb-4 text-left">
            <div className="inline-block p-4 rounded-[1.2rem] bg-[#2A2A2A] dark:bg-[#2A2A2A]">
              <img
                src={
                  document.documentElement.classList.contains('dark') ? typingWhite : typingBlack
                }
                alt="Typing..."
                className="w-[4rem] h-[2rem]"
              />
            </div>
          </div>
        )}

        {/* Scroll target for auto-scroll to bottom */}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input Area */}
      <div className="px-[2.7rem] pb-[2rem]">
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              type="text"
              name="message"
              placeholder="Type your message..."
              className="w-full bg-#FAFAFA dark:bg-[#2A2B2B] border border-[#DEDEDE]  dark:border-[#91E1C7] text-[#FFFFFF] placeholder:text-[#A3C5BA] px-5 py-4 pr-14 rounded-[1.2rem] text-[1.4rem] focus:outline-none focus:border-[#4BD2A7] transition-colors duration-200"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#FFFFFF] transition-colors duration-200"
              aria-label="Voice input"
            >
              <IoMicOutline className="size-6" />
            </button>
          </div>
        </form>

        {/* Footer Text */}
        <p className="text-center text-[#6B7280] text-[1rem] mt-3">Nezenx AI · Powered by AI</p>
      </div>
    </div>
  );
};

export default ChatMessages;
